import React, { useEffect, useMemo, useState } from 'react';
import '../../App.css';
import './compute-service.css';
import '../../tools/search-results/search-results.css';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// icons
import { ReactComponent as JupyterHUBIcon } from '../../icons/jupyter.svg';
import { ReactComponent as CartaIcon } from '../../icons/carta.svg';
import { ReactComponent as CanfarIcon } from '../../icons/canfar.svg';
import { ReactComponent as DesktopIcon } from '../../icons/virtual-desktop.svg';
import { ReactComponent as AladinIcon } from '../../icons/aladin.svg';
import headlessIcon from '../../icons/headless.512.png';
import { ReactComponent as SkyServerIcon } from '../../icons/skyserver.svg';
import {
  CircleArrowRight,
  ClockPlus,
  Circle,
  CircleAlert,
  CircleCheck,
  CircleQuestionMark,
  CircleX,
  Heart,
  Info,
  SquareTerminal,
  Trash2,
  TriangleAlert,
  Upload,
} from 'lucide-react';

// types.
import {
  CanfarServiceType,
  ComputeServiceType,
  JobType,
  UserType,
} from '../../utils/types';

// functions.
import { apiPrefix } from '../../utils/functions';

// components.
import VisualisationAladin from '../interactive-sessions/visualisation-aladin';
import VisualisationSkyServer from '../interactive-sessions/visualisation-skyserver';
import PleaseWait from '../../tools/please-wait';
import { AbbreviatedText } from '../../utils/functions';

//  --------------------------------------------------------------------------
//
//  R E D U X   S T A T E   M A N A G E M E N T
//
//  --------------------------------------------------------------------------

// import state definition and functions.
import {
  addTab,
  removeSessions,
  setSessionOverriddenStatus,
} from '../interactive-sessions/interactive-sessions-slice';
import {
  displaySession,
  launchCanfarSession,
  refreshCanfarServices,
  refreshSRCNetServices,
} from '../interactive-sessions/interactive-sessions-actions';
import { setShowNewSessionModal } from '../../app-slice';

//  --------------------------------------------------------------------------
//
//  T Y P E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  P R O P E R T I E S
//
//  --------------------------------------------------------------------------

function MainContent(props: {
  session: ComputeServiceType;
  pageVisible?: boolean;
  stagingJobs: JobType[];
}) {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  let status: string = props.session.compute_status;
  if (props.session.overridden_status !== undefined) {
    status = props.session.overridden_status;
  }
  const showSpinner: boolean = ['removing', 'renewing', 'pending', 'preparing'].includes(
    status,
  );

  // the 'session' passed in could be a genuine session in canfar, or an available compute service.
  const isSession: boolean = [
    'running',
    'pending',
    'preparing',
    'failed',
    'expired',
    'checking',
    'unknown',
    'error',
  ].includes(props.session.compute_status);

  const sessionName: string =
    (props.session.session_name ?? '') !== ''
      ? (props.session.session_name ?? '')
      : 'Unnamed ' + props.session.session_type?.toUpperCase() + ' service';

  const _activeStagingJobs: JobType[] = useMemo(() => {
    //
    return props.stagingJobs.filter((job: JobType) =>
      ['preparing', 'staging', 'retrying'].includes(job.status.toLowerCase()),
    );
    //
  }, [props.stagingJobs]);

  const STAGING_WIDTH: number = _activeStagingJobs.length > 0 ? 45 : 45;

  const WIDTH_OF_TEXT: number =
    isSession === true && props.session.resource_type === 'srcnet'
      ? 200 - STAGING_WIDTH
      : isSession === true &&
          ['canfar', 'broker'].includes(props.session.resource_type ?? '')
        ? 228 - STAGING_WIDTH
        : props.session.resource_type === 'srcnet'
          ? 205
          : 228;

  //  ------------------------------------------------------------
  //
  //  Displays the bottom banner depending on the banner type.
  //
  //  ------------------------------------------------------------

  function bottomBanner(args: { type: string | undefined; value: string | undefined }) {
    //
    // extract the date part from the first set of curly braces.
    function extractDate(value: string) {
      //
      // remove {} from the value.
      const startBrace = value.indexOf('{');
      const endBrace = value.indexOf('}');
      let dateValue: string = '';
      let remainder: string = value;
      if (startBrace > -1 && endBrace > -1 && endBrace > startBrace) {
        dateValue = value.substring(startBrace + 1, endBrace);
        remainder = value.substring(endBrace + 1, value.length);
      }
      if (startBrace === -1 && endBrace === -1) {
        dateValue = value;
      }

      return {
        dateValue: dateValue,
        remainder: remainder,
      };
      //
    } // extractDate

    var banner: string = args.value ?? '';

    if (args.type === 'until') {
      //
      // extract date.
      const toValue: {
        dateValue: string;
        remainder: string;
      } = extractDate(banner);

      const toDateTime: Date = new Date(toValue.dateValue);
      banner = 'until ' + toDateTime.toLocaleString();
    }

    if (args.type === 'from_to') {
      //
      // get the from date.
      const fromValue: {
        dateValue: string;
        remainder: string;
      } = extractDate(banner);

      // get the to date.
      const toValue: {
        dateValue: string;
        remainder: string;
      } = extractDate(fromValue.remainder);

      // convert to datetimes.
      const fromDateTime: Date = new Date(fromValue.dateValue);
      const toDateTime: Date = new Date(toValue.dateValue);

      // build banner.
      banner =
        'maintenance: ' +
        fromDateTime.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        }) +
        ' to ' +
        toDateTime.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        });
      //
    }

    return banner;
    //
  } // bottomBanner

  function GetServiceIcon(args: { type: string }) {
    //
    switch (args.type?.toLowerCase() ?? '') {
      case 'jupyterhub':
      case 'notebook':
        return <JupyterHUBIcon width={80} height={80} />;
      case 'canfar':
        return <CanfarIcon width={80} height={80} strokeWidth={6} />;
      case 'carta':
        return <CartaIcon width={80} height={80} />;
      case 'desktop':
        return <DesktopIcon width={80} height={80} strokeWidth={6} />;
      case 'aladinlite':
        return <AladinIcon width={80} height={80} />;
      case 'skyserver':
        return <SkyServerIcon width={80} height={80} strokeWidth={6} />;
      case 'headless':
        return <SquareTerminal width={80} height={80} strokeWidth={0.5} />;
      default:
        return <CircleQuestionMark width={80} height={80} strokeWidth={1} />;
    }
    //
  } // GetServiceIcon

  //  ------------------------------------------------------------
  //
  //  format a date that is in text format.
  //
  //  ------------------------------------------------------------

  function formatDate(args: { value: string }) {
    //
    let formattedDateTime: string = args.value;

    if (formattedDateTime !== '/') {
      let date: Date = new Date(args.value);
      formattedDateTime = date.toLocaleString();
    }

    // return something.
    return formattedDateTime;
    //
  } // formatDate

  //  --------------------------------------------------------------------------
  //
  //  H O O K S
  //
  //  --------------------------------------------------------------------------

  //  --------------------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  --------------------------------------------------------------------------

  return (
    <div
      style={{
        position: 'relative',
        textDecoration: 'none',
        color: 'inherit',
        cursor:
          [
            'notebook',
            'jupyterhub',
            'desktop',
            'carta',
            'canfar',
            'azimuth',
            'headless',
          ].indexOf(props.session.session_type) > -1
            ? 'pointer'
            : 'default',
        flex: '1 1',
        padding:
          '5px ' +
          (props.session.resource_type === 'srcnet' ? '5px' : '5px') /*30px*/ +
          ' ' +
          (props.session.compute_status === 'running' ||
          props.session.resource_type === 'srcnet' ||
          props.session.resource_type === 'canfar'
            ? '0px'
            : '0px') /*5px*/ +
          ' ' +
          (props.session.resource_type === 'srcnet' ? '5px' : '5px') /*30px*/,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {showSpinner && (
        <div className="session-spinner-overlay">
          <div className="spinner" />
        </div>
      )}
      <div className="session-header text-colour">
        <GetServiceIcon type={props.session.session_type} />
        <div
          className="fc"
          style={{
            flex: '0 0 ' + WIDTH_OF_TEXT.toString() + 'px',
            marginLeft: '10px',
          }}
        >
          <div
            className="session-name text-colour"
            style={{ width: WIDTH_OF_TEXT.toString() + 'px' }}
          >
            <AbbreviatedText
              text={props.session.service_name}
              pageVisible={props.pageVisible}
            />
          </div>
          {['running', 'pending'].includes(props.session.compute_status) && (
            <div
              className="session-name text-colour"
              style={{ width: WIDTH_OF_TEXT.toString() + 'px' }}
            >
              <AbbreviatedText text={sessionName} pageVisible={props.pageVisible} />
            </div>
          )}
          {['available', 'unavailable'].includes(props.session.compute_status) &&
            props.session.resource_type === 'srcnet' && (
              <div
                className="session-name text-colour"
                style={{ width: WIDTH_OF_TEXT.toString() + 'px' }}
              >
                [{props.session.session_type}]
              </div>
            )}
        </div>
        {(_activeStagingJobs.length > 0 ||
          ['error', 'failed'].includes(props.session.compute_status)) && (
          <div
            className="fc strobe-text"
            style={{
              width: '30px',
              margin: '5px 0px 0px 10px',
              height: '100%',
              textAlign: 'center',
              gap: '10px',
            }}
          >
            {_activeStagingJobs.length > 0 && (
              <Upload
                className="text-colour"
                data-text-orange="T"
                width={30}
                height={30}
                strokeWidth={1.5}
              />
            )}
            {['error', 'failed'].includes(props.session.compute_status) && (
              <TriangleAlert
                className="text-colour"
                data-text-red="T"
                width={30}
                height={30}
                strokeWidth={1.5}
              />
            )}
          </div>
        )}
        {props.session.health_status !== undefined &&
          props.session.resource_type === 'srcnet' && (
            <TrafficLights healthStatus={props.session.health_status} />
          )}
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          marginBottom: '10px',
        }}
      >
        <PleaseWait text="" visible={props.session.refreshing === true} small={true} />
        <div className="fc" style={{ flex: '1 1' }}>
          {props.session.compute_status === 'running' && (
            <div className="session-detail text-colour">
              expires: {formatDate({ value: props.session.expires_at ?? '/' })}
            </div>
          )}
          {['unavailable', 'pending'].includes(props.session.compute_status) && (
            <div className="text-colour">
              --- {props.session.compute_status.toUpperCase()} ---
            </div>
          )}
          {['removing'].includes(props.session.overridden_status ?? '') && (
            <div className="text-colour">
              --- {props.session.overridden_status?.toUpperCase() ?? ''} ---
            </div>
          )}
          {props.session.bottom_banner_type !== undefined && (
            <div
              className="fr text-colour"
              data-text-red={props.session.active_status === false ? 'T' : 'F'}
              data-text-orange={props.session.active_status !== false ? 'T' : 'F'}
              style={{
                flex: '0 0 auto',
                alignItems: 'center',
              }}
            >
              {props.session.active_status === false &&
              props.session.bottom_banner_type !== 'none' ? (
                <CircleAlert width={26} height={26} strokeWidth={1.5} />
              ) : props.session.bottom_banner_type !== 'none' ? (
                <Info width={26} height={26} strokeWidth={1.5} />
              ) : (
                <></>
              )}
              <div className="session-detail" style={{ flex: '1 1' }}>
                {props.session.refreshing !== true &&
                  bottomBanner({
                    type: props.session.bottom_banner_type,
                    value: props.session.bottom_banner,
                  })}
              </div>
            </div>
          )}
        </div>

        <div
          className="text-colour"
          style={{
            flex: '0 0 40px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
          }}
        >
          <CircleArrowRight width={40} height={40} strokeWidth={1} />
        </div>
      </div>
    </div>
  );
  //
} // MainContent

//  ------------------------------------------------------------
//
//  this column holds the traffic lights indicating the service
//  health.
//
//  ------------------------------------------------------------

function TrafficLights(args: { healthStatus: string }) {
  //

  //  --------------------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  --------------------------------------------------------------------------

  return (
    <div
      role="region"
      aria-label="Service health status"
      style={{
        flex: '0 0 20px',
        display: 'flex',
        height: '68px',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div
        className="search-compute-gray-border"
        style={{
          flex: '0 0 47px',
          width: '16px',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          borderBottom: 'none',
          borderRadius: '5px 5px 0px 0px',
          position: 'absolute',
        }}
      >
        <div style={{ flex: '0 0 1px' }}></div>
        {['active'].includes(args.healthStatus.toLowerCase()) && (
          <CircleCheck
            width={14}
            height={14}
            strokeWidth={4}
            className="text-colour"
            data-text-green="T"
          />
        )}
        {['active'].includes(args.healthStatus.toLowerCase()) === false && (
          <Circle
            width={14}
            height={14}
            strokeWidth={1}
            className="text-colour"
            data-muted="T"
          />
        )}
        <div style={{ flex: '0 0 1px' }}></div>
        {['unknown'].includes(args.healthStatus.toLowerCase()) && (
          <CircleQuestionMark
            width={14}
            height={14}
            strokeWidth={4}
            className="text-colour"
            data-text-orange="T"
          />
        )}
        {['unknown'].includes(args.healthStatus.toLowerCase()) === false && (
          <Circle
            width={14}
            height={14}
            strokeWidth={1}
            className="text-colour"
            data-muted="T"
          />
        )}
        <div style={{ flex: '0 0 1px' }}></div>
        {['inactive', 'shutdown'].includes(args.healthStatus.toLowerCase()) && (
          <CircleX
            width={14}
            height={14}
            strokeWidth={4}
            className="text-colour"
            data-text-red="T"
          />
        )}
        {['inactive', 'shutdown'].includes(args.healthStatus.toLowerCase()) === false && (
          <Circle
            width={14}
            height={14}
            strokeWidth={1}
            className="text-colour"
            data-muted="T"
          />
        )}
        <div style={{ flex: '0 0 6px' }}></div>
      </div>
      <div
        className="search-compute-gray-border"
        style={{
          flex: '0 0 20px',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: '2px 0px 0px 1px',
          borderRadius: '10px',
          position: 'relative',
          top: '47px',
        }}
      >
        <Heart
          width={14}
          height={14}
          strokeWidth={3}
          className="text-colour"
          data-text-blue="T"
        />
      </div>
    </div>
  );
  //
} // TrafficLights

//  ------------------------------------------------------------
//
//  Display a tile with an active or available compute session.
//
//  Note that the pageVisible parameter is required so that
//  abbreviated text items are re-checked for overflows once the
//  parent page becomes visible. In some cases they would have
//  been checked while the parent is hidden, and any overflow
//  would not have been detected.
//
//  ------------------------------------------------------------

export default function ComputeSession(props: {
  session: ComputeServiceType;
  pageVisible?: boolean;
  stagingCatalog?: string;
}) {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  const sD_canfarServices: CanfarServiceType[] = useAppSelector(
    (state) => state.data.canfarServices ?? [],
  );

  const sG_userType: UserType | undefined = useAppSelector(
    (state) => state.global.userType,
  );

  const sAllStagingJobs: JobType[] = useAppSelector(
    (state) => state.fileBrowser.dataManagementJobs,
  );

  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  const [sStagingJobs, setStagingJobs] = useState<JobType[]>([]);

  const canfarService: CanfarServiceType | undefined = sD_canfarServices.find(
    (service) => service.site.site_uuid === props.session.site.site_uuid,
  );

  //  --------------------------------------------------------------------------
  //
  //  F U N C T I O N S
  //
  //  --------------------------------------------------------------------------

  //  -------------------------------------------------
  //
  //  function that computes duration in days
  //
  //  -------------------------------------------------

  const computeSessionDuration = (): number => {
    //
    if (
      props.session.started_at !== undefined &&
      props.session.expires_at !== undefined
    ) {
      //
      const start_date = new Date(props.session.started_at);
      const end_date = new Date(props.session.expires_at);

      const diff = Math.floor(
        (end_date.getTime() - start_date.getTime()) / (1000 * 60 * 60 * 24),
      );
      const duration = Math.max(2, Math.round(diff));

      return duration;
      //
    } else {
      return -1;
    }
    //
  }; // computeSessionDuration

  //  ------------------------------------------------------------
  //
  //  Create a new compute session in the DB for a
  //  SRCNet compute session.
  //
  //  ------------------------------------------------------------

  async function createNewSession(args: { service: ComputeServiceType }) {
    //
    if (args.service.resource_type === 'canfar') {
      dispatch(
        setShowNewSessionModal({
          show: true,
          site_uuid: args.service.site.site_uuid,
          service_type: args.service.session_type,
          tab: args.service.resource_type,
          stagingCatalog: props.stagingCatalog,
        }),
      );
    } else if (args.service.resource_type === 'srcnet') {
      dispatch(
        setShowNewSessionModal({
          show: true,
          site_uuid: args.service.site.site_uuid,
          service_uuid: args.service.compute_service_id,
          tab: args.service.resource_type,
          stagingCatalog: props.stagingCatalog,
        }),
      );
    } else if (args.service.resource_type === 'broker') {
      dispatch(
        setShowNewSessionModal({
          show: true,
          service_type: args.service.session_type,
          tab: args.service.resource_type,
          stagingCatalog: props.stagingCatalog,
        }),
      );
    }
    //
  } // createNewSession

  //  ------------------------------------------------------------
  //
  //  Handler for DIV clicks.
  //
  //  ------------------------------------------------------------

  function handleSessionActivation(args: { infoDisplayed?: boolean }) {
    //
    // open a running service in a new tab. If the service status is pending, then we also
    // display the tab in 'info' mode.
    if (
      ['running', 'pending', 'error', 'failed', 'preparing'].includes(
        props.session.compute_status,
      )
    ) {
      displaySession({
        session: props.session,
        infoDisplayed:
          props.session.compute_status !== 'running' ||
          props.session.overridden_status === 'removing' ||
          args.infoDisplayed === true,
      });
    }

    // start an available service. STANDARD users bypass the launch dialog and
    // launch a CANFAR session directly with auto-populated values.
    if (
      ['canfar', 'srcnet', 'broker'].includes(props.session.resource_type ?? '#') &&
      ['jupyterhub', 'notebook', 'desktop', 'carta', 'headless'].includes(
        props.session.session_type,
      ) &&
      props.session.compute_status === 'available'
    ) {
      if (sG_userType === UserType.STANDARD && props.session.resource_type === 'canfar') {
        launchCanfarSession({
          service: props.session,
          stagingCatalog: props.stagingCatalog,
        });
      } else {
        createNewSession({ service: props.session });
      }
    }

    // open a canfar and azimuth service without displaying the dialog.
    if (
      (props.session.resource_type ?? '#') === 'srcnet' &&
      ['canfar', 'azimuth'].includes(props.session.session_type) &&
      props.session.compute_status === 'available'
    ) {
      displaySession({ session: props.session });
    }

    // open a web resource in a new tab on the page.
    if (props.session.session_type === 'aladinlite') {
      dispatch(
        addTab({
          service: props.session,
          toolComponent: VisualisationAladin,
        }),
      );
    }
    if (props.session.session_type === 'skyserver') {
      dispatch(
        addTab({
          service: props.session,
          toolComponent: VisualisationSkyServer,
        }),
      );
    }
    //
  } // handleSessionActivation

  //  -------------------------------------------------
  //
  //  Handler for btn clicks.
  //
  //  -------------------------------------------------

  const onClickBtnHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const button: HTMLButtonElement = event.currentTarget;

    // if the renew session button is clicked.
    if (button.name === 'btn-renew') {
      event.stopPropagation();
      renewComputeSession({ duration: computeSessionDuration() });
    }

    // if the remove session button is clicked.
    if (button.name === 'btn-remove') {
      event.stopPropagation();
      removeComputeSession();
    }

    // handle a click on the info button.
    if (button.name === 'btn-info') {
      event.stopPropagation();
      handleSessionActivation({ infoDisplayed: true });
    }

    // handle a click on the whole element.
    if (button.name === '') {
      handleSessionActivation({});
    }
    //
  }; // onClickBtnHandler

  //  -------------------------------------------------
  //
  //  Remove an active session
  //
  //  -------------------------------------------------

  async function removeComputeSession() {
    //
    // renew a SRCNet session.
    if (props.session.resource_type === 'srcnet') {
      //
      // update the status of the compute session, to set the overriden status to 'removing'.
      dispatch(
        setSessionOverriddenStatus({ sessionID: props.session.id, status: 'removing' }),
      );

      try {
        //
        const response = await fetch(
          apiPrefix() +
            `/v1/active_compute_sessions/remove?compute_session_id=${props.session.id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          },
        );

        // Giving backend time to propagate deletion
        await new Promise((resolve) => setTimeout(resolve, 300));

        const result = await response.json();
        if (!response.ok) {
          //
          // remove the temporary status of the compute session.
          dispatch(setSessionOverriddenStatus({ sessionID: props.session.id }));

          throw new Error(result.message || 'Failed to remove session');
          //
        } else {
          //
          // Refetch sessions.
          dispatch(refreshSRCNetServices());
          //
        }
        //
      } catch (err) {
        //
        console.error('Error removing compute session:', err);

        // remove the temporary status of the compute session.
        dispatch(setSessionOverriddenStatus({ sessionID: props.session.id }));
        //
      }
      //
    }

    // remove a CANFAR session.
    if (props.session.resource_type === 'canfar' && canfarService !== undefined) {
      //
      // update the status of the compute session, to set the overriden status to 'removing'.
      dispatch(
        setSessionOverriddenStatus({
          computeServiceID: props.session.compute_service_id,
          status: 'removing',
        }),
      );

      try {
        //
        const response = await fetch(
          apiPrefix() +
            `/v1/skaha/session/${props.session.compute_service_id}?url=${encodeURIComponent(canfarService.url)}`,
          {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          },
        );

        // Giving backend time to propagate renewal
        await new Promise((resolve) => setTimeout(resolve, 300));

        //const result = await response.json();
        if (response.status === 200) {
          //
          // Refetch sessions.
          /*dispatch(
            refreshCanfarServices({
              canfarService: canfarService,
              refreshAvailable: false,
            }),
          );*/
          // remove the compute session.
          dispatch(
            removeSessions({
              session_id: [props.session.compute_service_id],
            }),
          );
          //
        } else {
          //
          // remove the compute session.
          dispatch(
            removeSessions({
              session_id: [props.session.compute_service_id],
            }),
          );
          //
        }
        //
      } catch (err) {
        //
        // remove the compute session.
        dispatch(
          removeSessions({
            session_id: [props.session.compute_service_id],
          }),
        );
        //
      }
      //
    }
    //
  } // removeComputeSession

  //  -------------------------------------------------
  //
  //  Renew active session
  //
  //  -------------------------------------------------

  async function renewComputeSession(args: { duration: number }) {
    //
    // renew a SRCNet session.
    if (props.session.resource_type === 'srcnet') {
      //
      // update the status of the compute session, to set the overriden status to 'renewing'.
      dispatch(
        setSessionOverriddenStatus({ sessionID: props.session.id, status: 'renewing' }),
      );

      try {
        //
        const response = await fetch(
          apiPrefix() +
            `/v1/active_compute_sessions/renew?compute_session_id=${props.session.id}&duration=${args.duration}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          },
        );

        // Giving backend time to propagate renewal
        await new Promise((resolve) => setTimeout(resolve, 300));

        const result = await response.json();
        if (!response.ok) {
          //
          // remove the temporary status of the compute session.
          dispatch(setSessionOverriddenStatus({ sessionID: props.session.id }));

          throw new Error(result.message || 'Failed to renew session');
          //
        } else {
          //
          // Refetch sessions.
          dispatch(refreshSRCNetServices());
          //
        }
        //
      } catch (err) {
        //
        console.error('Error renewing compute session:', err);

        // remove the temporary status of the compute session.
        dispatch(setSessionOverriddenStatus({ sessionID: props.session.id }));
        //
      }
      //
    }

    // renew a CANFAR session.
    if (props.session.resource_type === 'canfar' && canfarService !== undefined) {
      //
      // update the status of the compute session, to set the overriden status to 'renewing'.
      dispatch(
        setSessionOverriddenStatus({
          computeServiceID: props.session.compute_service_id,
          status: 'renewing',
        }),
      );

      try {
        //
        const response = await fetch(
          apiPrefix() +
            `/v1/skaha/session/${props.session.compute_service_id}/renew?url=${encodeURIComponent(canfarService.url)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
          },
        );

        // Giving backend time to propagate renewal
        await new Promise((resolve) => setTimeout(resolve, 300));

        //const result = await response.json();
        if (response.status === 200) {
          //
          // Refetch sessions.
          dispatch(
            refreshCanfarServices({
              canfarService: canfarService,
              refreshAvailable: false,
            }),
          );
          //
        } else {
          //
          // remove the temporary status of the compute session.
          dispatch(
            setSessionOverriddenStatus({
              computeServiceID: props.session.compute_service_id,
            }),
          );

          throw new Error('Failed to renew session');
          //
        }
        //
      } catch (err) {
        //
        console.error('Error renewing compute session:', err);

        // remove the temporary status of the compute session.
        dispatch(
          setSessionOverriddenStatus({
            computeServiceID: props.session.compute_service_id,
          }),
        );
        //
      }
      //
    }
    //
  } // renewComputeSession

  //  ------------------------------------------------------------
  //
  //  H O O K S
  //
  //  ------------------------------------------------------------

  //  -------------------------------------------------
  //
  //  Get a list of staging jobs attached to this
  //  session.
  //
  //  -------------------------------------------------

  useEffect(() => {
    //
    // check what staging jobs are attached to this session.
    if (
      ['running', 'pending', 'preparing', 'error', 'failed'].includes(
        props.session.compute_status,
      )
    ) {
      //
      // get a list of staging jobs using the same compute service ID.
      const stagingJobs: JobType[] = sAllStagingJobs.filter(
        (job: JobType) => job.skaha_session_id === props.session.compute_service_id,
      );
      setStagingJobs(stagingJobs);
      //
    }
    //
  }, [props.session, sAllStagingJobs]);

  //  ------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  ------------------------------------------------------------

  const isRunning: boolean =
    ['running'].includes(props.session.compute_status) &&
    props.session.overridden_status !== 'removing';
  const isPending: boolean =
    ['pending', 'preparing'].includes(props.session.compute_status) &&
    props.session.overridden_status !== 'removing';
  const isFailed: boolean = ['failed', 'error'].includes(props.session.compute_status);
  const isStartable: boolean =
    ['canfar', 'srcnet', 'broker'].indexOf(props.session.resource_type ?? '#') > -1 &&
    props.session.compute_status === 'available';
  const isWebResource: boolean =
    ['aladinlite', 'skyserver'].indexOf(
      props.session.session_type?.toLowerCase() ?? '#',
    ) > -1;
  const isActionable: boolean =
    isRunning || isPending || isStartable || isWebResource || isFailed;

  return (
    <div className="compute-session-tile">
      <button
        aria-label={'Launch compute service'}
        className="compute-session-tile-main"
        onClick={onClickBtnHandler}
        disabled={!isActionable}
        title={
          props.session.session_name
            ? 'View ' + props.session.session_name
            : 'Launch ' + props.session.service_name
        }
      >
        {/* main section */}
        <MainContent
          key={props.session.service_name + props.session.session_name}
          session={props.session}
          pageVisible={props.pageVisible}
          stagingJobs={sStagingJobs}
        />
      </button>
      {['available', 'unavailable'].includes(props.session.compute_status) === false && (
        <div className="compute-session-tile-actions">
          <button
            className="text-colour"
            name="btn-remove"
            type="button"
            title="Remove session"
            style={{
              backgroundColor: 'transparent',
              border: 'none',
            }}
            onClick={onClickBtnHandler}
            disabled={isRunning === false && isPending === false && isFailed === false}
          >
            <Trash2 width={30} height={30} strokeWidth={1.5} />
          </button>
          <div style={{ flex: '0 0 10px' }} />
          <button
            className="text-colour"
            name="btn-renew"
            type="button"
            title="Renew session"
            style={{
              backgroundColor: 'transparent',
              border: 'none',
            }}
            onClick={onClickBtnHandler}
            disabled={isRunning === false}
          >
            <ClockPlus width={30} height={30} strokeWidth={1.5} />
          </button>
          <div style={{ flex: '1 1' }} />
          <button
            className="text-colour"
            name="btn-info"
            type="button"
            title="View session information"
            style={{
              backgroundColor: 'transparent',
              border: 'none',
            }}
            onClick={onClickBtnHandler}
            disabled={isRunning === false && isPending === false && isFailed === false}
          >
            <Info width={30} height={30} strokeWidth={1.5} />
          </button>
        </div>
      )}
    </div>
  );
  //
} // ComputeSession
